import { CloudUploadOutlined, DeleteOutlined, EditOutlined, LoginOutlined, SaveOutlined } from '@ant-design/icons';
import { EllipseContainer } from '@smx-ui-common/ellipseContainer';
import { highlightSearchText } from '@smx-ui-common/function';
import { ButtonAdd } from '@smx-ui-inspector/button-add';
import { LinkToInspectorEntity } from '@smx-ui-inspector/link-to-inspector-entity';
import { useFragmentFromStore } from '@smx-ui-model/hooks/use-fragment-from-store';
import { useAccess } from '@smx-ui-permission/hook/use-access';
import { Button, Flex, Tag } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { drawingSetFragment$key } from '../graphql/__generated__/drawingSetFragment.graphql';
import { JobStatus } from '../graphql/__generated__/drawingSetJobFragment.graphql';
import { DrawingSetModelType } from '../model/types';
import { AppDispatch, RootState } from '../store';
import {
    IsUpdateAfterUploadDrawingSetVersionType,
    setIsUpdateAfterUploadDrawingSetVersion,
    setterDrawings,
} from '../store/drawings-slice';
import { IndicatorStatusDrawingSet } from './indicator-status-drawing-set';

interface CollapseListDrawingSetHeaderProps {
    node: any;
    listFieldName: (keyof DrawingSetModelType)[];
    storeLinkBody?: React.MutableRefObject<any>;
    openCollapseKey: string[];
}

export const CollapseListDrawingSetHeader = ({
    node,
    listFieldName = [],
    storeLinkBody,
    openCollapseKey,
}: CollapseListDrawingSetHeaderProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const isUpdateAfterUploadDrawingSetVersion = useSelector<RootState, IsUpdateAfterUploadDrawingSetVersionType>(
        (state) => state.drawings.isUpdateAfterUploadDrawingSetVersion,
    );
    const currentSearchText = useSelector<RootState, string | undefined>((state) => state.drawings.currentSearchText);
    const formDrawingSheetWasChanged = useSelector<RootState, IsUpdateAfterUploadDrawingSetVersionType>(
        (state) => state.drawings.formDrawingSheetWasChanged,
    );
    const currentTags = useSelector<RootState, string[]>((state) => state.drawings.currentTags);

    const [statusButton, setStatusButton] = useState(true);
    const [isInFlight, setIsInFlight] = useState();
    const [jobIndicatorStatus, setJobIndicatorStatus] = useState<JobStatus>();

    const storeInterval = useRef<number | NodeJS.Timeout>(undefined);
    const dataNode = useFragmentFromStore<drawingSetFragment$key>(node);
    const navigate = useNavigate();

    const accessUpload = useAccess(['drawing.all.create', 'drawing.all.edit', 'drawing.all.manage']);
    const accessSaving = useAccess(['drawing.all.edit', 'drawing.all.manage']);
    const accessDeleting = useAccess(['drawing.all.manage']);

    useEffect(() => {
        if (formDrawingSheetWasChanged[dataNode.id]) {
            setStatusButton(false);
        }
    }, [formDrawingSheetWasChanged]);

    useEffect(() => {
        if (isUpdateAfterUploadDrawingSetVersion[dataNode.id]) {
            setJobIndicatorStatus(undefined);
            dispatch(setIsUpdateAfterUploadDrawingSetVersion({ [dataNode.id]: false }));
        }
    }, [isUpdateAfterUploadDrawingSetVersion[dataNode.id]]);

    useEffect(() => {
        if (openCollapseKey.includes(node.__id)) {
            if (storeInterval.current) {
                clearInterval(storeInterval.current);
            }

            storeInterval.current = setInterval(() => {
                setStatusButton(!Object.keys(storeLinkBody?.current?.[node.__id]?.current?.changedValues || {}).length);
                setIsInFlight(storeLinkBody?.current?.[node.__id]?.current?.isInFlight);
            }, 1000);
        } else {
            clearInterval(storeInterval.current);
        }
    }, [openCollapseKey]);

    const listTag = dataNode?.tags?.split(',') || [];

    return (
        <Flex justify={'space-between'}>
            <Flex>
                <div style={{ width: 250, paddingRight: 10 }}>
                    <EllipseContainer>
                        {jobIndicatorStatus === 'FAILED' && <Tag color="error">Failed</Tag>}
                        {highlightSearchText(
                            listFieldName.map((fieldName) => (dataNode as any)[fieldName]).join(' '),
                            currentSearchText || '',
                        )}
                    </EllipseContainer>
                </div>
                <Flex justify={'start'}>
                    {[...listTag]
                        .sort((a, b) => {
                            const aInCurrent = currentTags.includes(a);
                            const bInCurrent = currentTags.includes(b);
                            if (aInCurrent && !bInCurrent) return -1;
                            if (!aInCurrent && bInCurrent) return 1;
                            return 0;
                        })
                        .map((tag) => (
                            <Tag
                                key={tag}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const newTags = currentTags.includes(tag)
                                        ? currentTags.filter((currentTag) => currentTag !== tag)
                                        : [...currentTags, tag];
                                    dispatch(setterDrawings({ currentTags: newTags }));
                                }}
                                color={currentTags.includes(tag) ? 'orange-inverse' : 'default'}
                            >
                                {highlightSearchText(tag, currentSearchText || '')}
                            </Tag>
                        ))}
                </Flex>
            </Flex>
            <Flex gap={10} justify={'space-between'} style={{ minWidth: 10, minHeight: 24 }}>
                {accessUpload && (
                    <LinkToInspectorEntity
                        node={node}
                        icon
                        component={(onClick) => (
                            <Button size={'small'} onClick={onClick} icon={<EditOutlined />}></Button>
                        )}
                    />
                )}
                <Button
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate({
                            pathname: `/drawingset/${node.__id}/${dataNode.versionDate}`,
                            search: location.search,
                        });
                    }}
                    icon={<LoginOutlined />}
                    size={'small'}
                    style={{ minWidth: 'auto', width: '30px' }}
                    disabled={!dataNode.versionDate}
                ></Button>
                {jobIndicatorStatus === 'SUCCESS' && (
                    <>
                        {accessSaving && (
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    storeLinkBody?.current?.[node.__id]?.current.submitTableChanges();
                                }}
                                icon={<SaveOutlined />}
                                loading={isInFlight}
                                size={'small'}
                                disabled={statusButton}
                                style={{ minWidth: 'auto', width: '30px' }}
                            ></Button>
                        )}
                        {accessUpload && (
                            <ButtonAdd
                                icon={<CloudUploadOutlined />}
                                size={'small'}
                                modelName={'drawingSetVersion'}
                                parentData={{
                                    drawingSetId: node.__id,
                                }}
                                minWidth={'auto'}
                            />
                        )}
                    </>
                )}
                {accessDeleting && jobIndicatorStatus === 'FAILED' && (
                    <Button
                        variant={'outlined'}
                        color={'red'}
                        onClick={(e) => {
                            e.stopPropagation();
                            console.log('remove');
                        }}
                        icon={<DeleteOutlined />}
                        size={'small'}
                        style={{ minWidth: 'auto', width: '30px' }}
                    />
                )}
                {jobIndicatorStatus !== 'FAILED' && jobIndicatorStatus !== 'SUCCESS' && (
                    <Flex style={{ marginLeft: 10 }}>
                        <IndicatorStatusDrawingSet node={node} setJobIndicatorStatus={setJobIndicatorStatus} />
                    </Flex>
                )}
            </Flex>
        </Flex>
    );
};
